<script setup>
// The history of the prompt, in two tabs. Versions: one per publication, with who published, when, why, what it
// changed and how the publication went; any but the latest can be restored (it becomes a new version). Log: every
// change, publication, send to the voice provider and drift found, with the value before and after. Nothing here
// can be edited or removed.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-next/polaris/ui/tabs';
import PaginationFooter from 'dashboard/components-next/polaris/cards/PaginationFooter.vue';

const props = defineProps({
  versions: { type: Object, required: true }, // { items, total, page }
  auditEvents: { type: Object, required: true }, // { items, total, page }
  pageSize: { type: Number, default: 25 },
  busy: Boolean,
});
const emit = defineEmits(['restore', 'versionsPage', 'auditEventsPage']);

const { t, te, locale } = useI18n();
const tab = ref('versions');
const BASE = 'POLARIS.ASSISTANTS.VOICE';

const dateFormat = computed(
  () =>
    new Intl.DateTimeFormat(String(locale.value).replace('_', '-'), {
      dateStyle: 'short',
      timeStyle: 'short',
    })
);
const when = seconds => dateFormat.value.format(new Date(seconds * 1000));
const who = person =>
  person?.name || t('POLARIS.ASSISTANTS.VOICE.HISTORY.SYSTEM');

// "prompt_blocks.product_sheet" -> the field's name on the screen; an unknown block keeps its own name.
const blockLabel = block => {
  const key = `${BASE}.BLOCKS.${block.replace('.', '_').toUpperCase()}`;
  return te(key) ? t(key) : block;
};

const SYNC_VARIANTS = {
  synced: 'success',
  recorded: 'secondary',
  partial: 'warning',
  failed: 'destructive',
};

const latestVersionNo = computed(() =>
  props.versions.page === 1 ? props.versions.items[0]?.version_no : null
);

const versionRows = computed(() =>
  props.versions.items.map(version => ({
    version,
    id: version.id,
    title: t('POLARIS.ASSISTANTS.VOICE.HISTORY.VERSION', {
      version: version.version_no,
    }),
    byline: t('POLARIS.ASSISTANTS.VOICE.HISTORY.BYLINE', {
      name: who(version.published_by),
      time: when(version.created_at),
    }),
    restoredFrom:
      version.publish_origin === 'restore'
        ? t('POLARIS.ASSISTANTS.VOICE.HISTORY.RESTORED_FROM', {
            version: version.restored_from_version_no,
          })
        : '',
    changed: version.changed_blocks.length
      ? t('POLARIS.ASSISTANTS.VOICE.HISTORY.CHANGED', {
          blocks: version.changed_blocks.map(blockLabel).join(', '),
        })
      : '',
    syncVariant: SYNC_VARIANTS[version.sync_status] || 'outline',
    syncLabel: t(`${BASE}.SYNC.${version.sync_status.toUpperCase()}`),
    canRestore: version.version_no !== latestVersionNo.value,
  }))
);

const emptyValue = computed(() =>
  t('POLARIS.ASSISTANTS.VOICE.HISTORY.EMPTY_VALUE')
);

const eventRows = computed(() =>
  props.auditEvents.items.map(event => ({
    id: event.id,
    action: t(`${BASE}.ACTIONS.${event.action.toUpperCase()}`),
    block: event.block ? blockLabel(event.block) : '',
    version: event.version_no
      ? t('POLARIS.ASSISTANTS.VOICE.HISTORY.VERSION', {
          version: event.version_no,
        })
      : '',
    byline: t('POLARIS.ASSISTANTS.VOICE.HISTORY.BYLINE', {
      name: who(event.actor),
      time: when(event.created_at),
    }),
    reason: event.reason || '',
    // The value before and after, side by side; an empty side says so.
    sides:
      event.value_before || event.value_after
        ? [
            {
              key: 'before',
              label: t('POLARIS.ASSISTANTS.VOICE.HISTORY.BEFORE'),
              value: event.value_before || emptyValue.value,
            },
            {
              key: 'after',
              label: t('POLARIS.ASSISTANTS.VOICE.HISTORY.AFTER'),
              value: event.value_after || emptyValue.value,
            },
          ]
        : [],
    truncated: event.truncated,
  }))
);
</script>

<template>
  <Tabs v-model="tab">
    <TabsList>
      <TabsTrigger value="versions">
        {{ t('POLARIS.ASSISTANTS.VOICE.HISTORY.VERSIONS_TAB') }}
      </TabsTrigger>
      <TabsTrigger value="log">
        {{ t('POLARIS.ASSISTANTS.VOICE.HISTORY.LOG_TAB') }}
      </TabsTrigger>
    </TabsList>

    <TabsContent value="versions" class="grid gap-3">
      <p
        v-if="versionRows.length === 0"
        class="type-polaris-body text-polaris-muted-foreground"
        data-voice-no-versions
      >
        {{ t('POLARIS.ASSISTANTS.VOICE.HISTORY.NO_VERSIONS') }}
      </p>
      <ul v-else class="m-0 grid list-none gap-0 p-0">
        <li
          v-for="row in versionRows"
          :key="row.id"
          class="flex flex-wrap items-start justify-between gap-3 border-0 border-b border-solid border-polaris-border py-3 last:border-b-0"
          :data-voice-version="row.version.version_no"
        >
          <div class="grid min-w-0 flex-1 gap-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="type-polaris-label">{{ row.title }}</span>
              <Badge :variant="row.syncVariant">{{ row.syncLabel }}</Badge>
              <span
                v-if="row.restoredFrom"
                class="type-polaris-caption text-polaris-muted-foreground"
              >
                {{ row.restoredFrom }}
              </span>
            </div>
            <p class="break-words type-polaris-body">
              {{ row.version.change_summary }}
            </p>
            <p class="type-polaris-caption text-polaris-muted-foreground">
              {{ row.byline }}
            </p>
            <p
              v-if="row.changed"
              class="break-words type-polaris-caption text-polaris-muted-foreground"
            >
              {{ row.changed }}
            </p>
          </div>
          <Button
            v-if="row.canRestore"
            variant="outline"
            size="sm"
            :disabled="busy"
            @click="emit('restore', row.version)"
          >
            {{ t('POLARIS.ASSISTANTS.VOICE.HISTORY.RESTORE') }}
          </Button>
        </li>
      </ul>
      <PaginationFooter
        v-if="versions.total > pageSize"
        :page="versions.page"
        :total="versions.total"
        :page-size="pageSize"
        count-key="POLARIS.ASSISTANTS.VOICE.HISTORY.VERSIONS_COUNT"
        showing-key="POLARIS.ASSISTANTS.VOICE.HISTORY.VERSIONS_SHOWING"
        @update:page="page => emit('versionsPage', page)"
      />
    </TabsContent>

    <TabsContent value="log" class="grid gap-3">
      <p
        v-if="eventRows.length === 0"
        class="type-polaris-body text-polaris-muted-foreground"
        data-voice-no-events
      >
        {{ t('POLARIS.ASSISTANTS.VOICE.HISTORY.NO_EVENTS') }}
      </p>
      <ul v-else class="m-0 grid list-none gap-0 p-0">
        <li
          v-for="row in eventRows"
          :key="row.id"
          class="grid gap-1 border-0 border-b border-solid border-polaris-border py-3 last:border-b-0"
          data-voice-event
        >
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span class="type-polaris-label">{{ row.action }}</span>
            <span v-if="row.block" class="type-polaris-body">
              {{ row.block }}
            </span>
            <span
              v-if="row.version"
              class="type-polaris-caption text-polaris-muted-foreground"
            >
              {{ row.version }}
            </span>
          </div>
          <p v-if="row.reason" class="break-words type-polaris-body">
            {{ row.reason }}
          </p>
          <p class="type-polaris-caption text-polaris-muted-foreground">
            {{ row.byline }}
          </p>
          <details v-if="row.sides.length" class="type-polaris-caption">
            <summary class="cursor-pointer text-polaris-foreground">
              {{ t('POLARIS.ASSISTANTS.VOICE.HISTORY.SHOW_VALUES') }}
            </summary>
            <div class="mt-2 grid gap-2 lg:grid-cols-2">
              <div
                v-for="side in row.sides"
                :key="side.key"
                class="grid content-start gap-1"
              >
                <span class="text-polaris-muted-foreground">
                  {{ side.label }}
                </span>
                <pre
                  class="m-0 max-h-60 overflow-y-auto whitespace-pre-wrap break-words rounded-polaris-md border border-solid border-polaris-border bg-polaris-muted p-2 text-polaris-foreground"
                ><code>{{ side.value }}</code></pre>
              </div>
            </div>
            <p v-if="row.truncated" class="mt-2 text-polaris-muted-foreground">
              {{ t('POLARIS.ASSISTANTS.VOICE.HISTORY.TRUNCATED') }}
            </p>
          </details>
        </li>
      </ul>
      <PaginationFooter
        v-if="auditEvents.total > pageSize"
        :page="auditEvents.page"
        :total="auditEvents.total"
        :page-size="pageSize"
        count-key="POLARIS.ASSISTANTS.VOICE.HISTORY.EVENTS_COUNT"
        showing-key="POLARIS.ASSISTANTS.VOICE.HISTORY.EVENTS_SHOWING"
        @update:page="page => emit('auditEventsPage', page)"
      />
    </TabsContent>
  </Tabs>
</template>
